// ---------------------------------------------------------------------------
// The lead's assessment card: its view model (NDI-239 v2)
//
// Pure, so it tests without a page:
//   - Leads carry no website (campaign-detail's LeadData has none), so the
//     card suggests the lead's email domain — unless it is a freemail
//     provider's: gmail.com is nobody's school.
//   - Which assessment is the lead's (the newest), whether it still runs,
//     its status as a dot and a word.
//   - What a checked one shows: 2–3 key numbers — the hand-over carries raw
//     numbers today and, additively in v2, {label, text, value} objects; both
//     are read — and the inputs split into found (with source and quote) and
//     assumed (with basis).
// ---------------------------------------------------------------------------

import type { StatusLook } from '../campaign-status.js';
import type { AssessmentView, RawKeyNumber, SetupView } from './api.js';
import { formatNumber, labelWithoutUnit, readSteps } from './setup-model.js';

/**
 * Freemail providers (spec §7). Entries without a dot are providers with
 * several country domains (gmx.de, gmx.net, gmx.at …): the domain's first
 * label must match exactly. Entries with a dot match the domain exactly.
 */
export const FREEMAIL_DOMAINS: readonly string[] = [
  'gmail',
  'googlemail',
  'gmx',
  'web.de',
  't-online',
  'outlook',
  'hotmail',
  'live',
  'yahoo',
  'icloud',
  'me',
  'aol',
  'freenet',
  'posteo',
  'mail.de',
  'arcor',
  'protonmail',
  'proton.me',
];

/** The domain of an email address, lower-case; '' when there is none. */
export function emailDomain(email: string | undefined): string {
  const at = (email ?? '').trim().toLowerCase().split('@');
  if (at.length !== 2 || !at[0]) return '';
  return at[1] ?? '';
}

function isFreemail(domain: string): boolean {
  const first = domain.split('.')[0] ?? '';
  return FREEMAIL_DOMAINS.some(f => (f.includes('.') ? domain === f : first === f));
}

/** The website to prefill: the email's domain, unless it is freemail or not a domain at all. */
export function suggestWebsite(email: string | undefined): string {
  const domain = emailDomain(email);
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain)) return '';
  return isFreemail(domain) ? '' : domain;
}

/** The lead's newest assessment. */
export function latestForLead(records: AssessmentView[], leadId: string): AssessmentView | undefined {
  return records
    .filter(r => r.leadId === leadId)
    .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')))[0];
}

const RUNNING = new Set(['researching', 'calculated', 'rendered']);

/** A pipeline is still working on it — the card polls. */
export function isRunning(status: string | undefined): boolean {
  return !!status && RUNNING.has(status);
}

// A still dot in the info tone while it runs: the card's spinner says it works, and the pulsing dot would be the
// success teal of "Checked" (campaign-status.ts draws `live` in that colour).
const LOOKS: Record<string, StatusLook> = {
  researching: { tone: 'info', label: 'Researching' },
  calculated: { tone: 'info', label: 'Calculated' },
  rendered: { tone: 'info', label: 'Making PDF' },
  checked: { tone: 'positive', label: 'Checked' },
  blocked: { tone: 'critical', label: 'Blocked' },
};

export function assessmentLook(status: string | undefined): StatusLook {
  return (
    (status && Object.hasOwn(LOOKS, status) ? LOOKS[status] : undefined) ?? {
      tone: 'neutral',
      label: status || 'Unknown',
    }
  );
}

/**
 * The setups an assessment can start from: approved, and — when the server sends its steps — with the approval step
 * still done. A setup changed since its approval keeps the word 'approved' but the server refuses it (409
 * SETUP_NOT_APPROVED), so it is not offered.
 */
export function approvedSetups(setups: SetupView[]): SetupView[] {
  return setups.filter(
    s =>
      s.approval?.status === 'approved' &&
      (s.steps?.approved === undefined || readSteps(s.steps).approved.state === 'done'),
  );
}

export interface KeyNumber {
  key: string;
  label: string;
  text: string;
}

const MAX_KEY_NUMBERS = 3;

/** Up to three key numbers, from raw numbers or {label, text, value} objects, in a map or a list. */
export function keyNumbers(
  raw: Record<string, RawKeyNumber> | RawKeyNumber[] | undefined,
  record: Pick<AssessmentView, 'tool'> | undefined,
): KeyNumber[] {
  if (!raw || typeof raw !== 'object') return [];
  const entries: [string, RawKeyNumber][] = Array.isArray(raw)
    ? raw.map((v, i) => [(v && typeof v === 'object' && v.key) || String(i), v])
    : Object.entries(raw);
  const out: KeyNumber[] = [];
  for (const [key, value] of entries) {
    if (out.length >= MAX_KEY_NUMBERS) break;
    const fallbackLabel = record?.tool?.outputs?.[key]?.label ?? key;
    if (typeof value === 'number' && Number.isFinite(value)) {
      out.push({ key, label: fallbackLabel, text: formatNumber(value) });
    } else if (value && typeof value === 'object') {
      const text =
        typeof value.text === 'string' && value.text.trim()
          ? value.text.trim()
          : typeof value.value === 'number' && Number.isFinite(value.value)
            ? formatNumber(value.value)
            : '';
      if (text) out.push({ key, label: withoutShownUnit(value.label?.trim() || fallbackLabel, text), text });
    }
  }
  return out;
}

/** "Ersparnis pro Jahr (€)" over "146.016 €" → "Ersparnis pro Jahr": the bracketed unit goes when the text ends in it. */
function withoutShownUnit(label: string, text: string): string {
  const unit = /\(([^()]+)\)\s*$/.exec(label)?.[1]?.trim();
  return unit && text.trim().endsWith(unit) ? labelWithoutUnit(label, unit) : label;
}

/** The key numbers of a hand-over: its keyNumberDetails (German, formatted by the server) when it has them, else keyNumbers. */
export function handoverKeyNumbers(record: Pick<AssessmentView, 'handover' | 'tool'>): KeyNumber[] {
  const details = record.handover?.keyNumberDetails;
  const fromDetails = Array.isArray(details) && details.length > 0 ? keyNumbers(details, record) : [];
  return fromDetails.length > 0 ? fromDetails : keyNumbers(record.handover?.keyNumbers, record);
}

/** Why a start was refused, as the card shows it. */
export type StartRefusal =
  | { kind: 'running'; assessmentId: string }
  | { kind: 'setup'; reason: string }
  | { kind: 'error'; reason: string };

const SETUP_CODES = new Set(['SETUP_NOT_APPROVED', 'SETUP_REQUIRED']);

/**
 * A refused start, read by its code first: SETUP_NOT_APPROVED (409) and SETUP_REQUIRED (400) are a setup problem —
 * the reason is the server's German sentence; only a 409 that names an assessmentId means one is already running.
 */
export function startRefusal(status: number, body: Record<string, unknown>, message: string): StartRefusal {
  const reason = typeof body.error === 'string' && body.error.trim() ? body.error.trim() : message;
  if (typeof body.code === 'string' && SETUP_CODES.has(body.code)) return { kind: 'setup', reason };
  if (status === 409 && typeof body.assessmentId === 'string' && body.assessmentId) {
    return { kind: 'running', assessmentId: body.assessmentId };
  }
  return { kind: 'error', reason };
}

export interface FoundFact {
  key: string;
  label: string;
  value: string;
  /** The source as a link — '' when it is not http(s), so it is never made one. */
  url: string;
  /** The source as text: host and path. */
  host: string;
  quote: string;
}

export interface AssumedFact {
  key: string;
  label: string;
  value: string;
  basis: string;
}

function sourceLink(url: string): { url: string; host: string } {
  try {
    const u = new URL(url);
    if (u.protocol === 'http:' || u.protocol === 'https:') {
      return { url, host: `${u.host}${u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '')}` };
    }
  } catch {
    // Not a URL: shown as text.
  }
  return { url: '', host: url };
}

/** A short unit in brackets at the end of a label — "Stundensatz Lehrkraft (€)" gives "€"; '' when there is none. */
function trailingUnit(label: string): string {
  return /\((€|%|Std\.)\)\s*$/.exec(label)?.[1] ?? '';
}

/**
 * The inputs, split into what was read on the website and what was assumed. A label's unit in brackets moves to its
 * value ("52 €" and "Stundensatz Lehrkraft"), as on the key numbers; a percent stays as the value says (no ×100).
 */
export function factLists(record: Pick<AssessmentView, 'inputs'>): { found: FoundFact[]; assumed: AssumedFact[] } {
  const found: FoundFact[] = [];
  const assumed: AssumedFact[] = [];
  for (const input of record.inputs ?? []) {
    const unit = trailingUnit(input.label ?? '');
    const value = formatNumber(input.value, unit ? { unit } : undefined);
    const label = unit ? labelWithoutUnit(input.label, unit) : input.label;
    if (input.kind === 'found') {
      found.push({
        key: input.key,
        label,
        value,
        ...sourceLink(input.source?.url ?? ''),
        quote: input.source?.quote ?? '',
      });
    } else if (input.kind === 'estimated') {
      assumed.push({ key: input.key, label, value, basis: input.basis ?? '' });
    }
  }
  return { found, assumed };
}
